import type { PaginationState } from '@tanstack/react-table';
import axios from 'axios';

import type { QueueFilters, QueueMessageId, QueueRequest, QueueStatus, QueueType } from './models';
import {
	queueItemLogSchema,
	queueMessagePaginationResultSchema,
	queueMessageSchema,
	queueSchema,
} from './models';

export async function getQueues(filters: QueueFilters = {}, signal?: AbortSignal) {
	const { data } = await axios.get<unknown>('/api/management/queue-messages/GetAll', {
		params: filters,
		signal,
	});

	return queueSchema.array().parse(data);
}

export async function getQueueMessages(
	queue: QueueType,
	status: QueueStatus,
	pagination: PaginationState,
	filters: QueueFilters = {},
	signal?: AbortSignal,
) {
	const encodedQueue = encodeURIComponent(queue);
	const encodedStatus = encodeURIComponent(status.toLowerCase());

	const request: QueueRequest = {
		currentPage: pagination.pageIndex + 1,
		pageSize: pagination.pageSize,
		filters,
	};

	const { data } = await axios.get<unknown>(
		`/api/management/queue-messages/${encodedQueue}/${encodedStatus}`,
		{
			params: {
				currentPage: request.currentPage,
				pageSize: request.pageSize,
				'Filters.LastAttemptOn': request.filters?.lastAttemptOn,
				'Filters.CompletedOn': request.filters?.completedOn,
			},
			signal,
		},
	);

	return queueMessagePaginationResultSchema.parse(data);
}

export async function getQueueMessage(messageId: QueueMessageId, signal?: AbortSignal) {
	const encodedMessageId = encodeURIComponent(messageId);

	const { data } = await axios.get<unknown>(
		`/api/management/queue-messages/${encodedMessageId}`,
		{
			signal,
		},
	);

	return queueMessageSchema.parse(data);
}

export async function getQueueMessageLogs(messageId: QueueMessageId, signal?: AbortSignal) {
	const encodedMessageId = encodeURIComponent(messageId);

	const { data } = await axios.get<unknown>(
		`/api/management/queue-messages/${encodedMessageId}/logs`,
		{
			signal,
		},
	);

	return queueItemLogSchema.array().parse(data);
}

export async function requeueMessage(messageId: QueueMessageId) {
	const encodedMessageId = encodeURIComponent(messageId);

	await axios.post(`/api/management/queue-messages/${encodedMessageId}/requeue`);
}

export async function abandonMessage(messageId: QueueMessageId) {
	const encodedMessageId = encodeURIComponent(messageId);

	await axios.post(`/api/management/queue-messages/${encodedMessageId}/abandon`);
}
